/**
 * Completion Step
 *
 * Final step with context-aware CTA and navigation.
 * Issue #563 - First-Run Onboarding Flow
 */

import { ChevronLeft } from 'lucide-react';
import type { OnboardingContext } from '@/lib/api/auth';
import PhotoAttribution from '@/components/PhotoAttribution';
import ImageWithSkeleton from '@/components/ImageWithSkeleton';

interface CompletionStepProps {
  context: OnboardingContext;
  clubContext?: string | null; // Club ID if created during onboarding (Path B1)
  onComplete: () => void;
  onBack: () => void;
  isSubmitting: boolean;
}

export default function CompletionStep({
  context,
  clubContext,
  onComplete,
  onBack,
  isSubmitting
}: CompletionStepProps) {
  // Context-aware content
  const getContent = () => {
    // Path B1: User created a club during onboarding
    if (clubContext) {
      return {
        title: 'Your club is ready!',
        cta: 'Go to Club'
      };
    }

    switch (context.type) {
      case 'club':
        return {
          title: 'You\'re all set!',
          cta: 'Go to Club'
        };
      case 'event':
        return {
          title: 'You\'re ready!',
          cta: 'View Event'
        };
      case 'founder':
        return {
          title: 'Time to build',
          cta: context.club_id ? 'Go to Club' : 'Create Club'
        };
      case 'clean':
      default:
        return {
          title: 'Welcome aboard!',
          cta: 'Explore'
        };
    }
  };

  const { title, cta } = getContent();

  return (
    <div className="flex flex-col flex-1 min-h-0">
      {/* Progress Bar - Complete */}
      <div className="shrink-0 px-6 pt-4">
        <div className="w-full h-1 bg-oro-surface-alt rounded-full overflow-hidden">
          <div
            className="h-full bg-oro-gold rounded-full transition-all duration-300"
            style={{ width: '100%' }}
          />
        </div>
        <p className="text-text-muted text-xs mt-2 text-center">Complete</p>
      </div>

      {/* Hero Image - Full Height */}
      <div className="flex-1 relative m-4 rounded-xl overflow-hidden">
        <ImageWithSkeleton
          src="/images/onboarding/hero-vertical-forest-runner-action-gregrosenke_cp.jpg"
          alt="Runner in motion through a forest trail"
          loading="eager"
          className="w-full h-full object-cover"
        />
        <PhotoAttribution photographer="Greg Rosenke" unsplashUsername="greg_rosenke" />

        {/* Title overlay on image */}
        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent p-6">
          <h2 id="onboarding-title" className="text-2xl font-semibold text-white">
            {title}
          </h2>
        </div>
      </div>

      {/* Bottom-Docked Actions */}
      <div className="shrink-0 p-4 border-t border-oro-border bg-oro-surface">
        <div className="flex gap-3">
          <button
            type="button"
            onClick={onBack}
            disabled={isSubmitting}
            className="px-4 py-3 rounded-lg bg-oro-surface-alt border border-oro-border text-text-primary hover:bg-oro-surface-alt/80 transition-colors disabled:opacity-50"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            type="button"
            onClick={onComplete}
            disabled={isSubmitting}
            className="flex-1 px-4 py-3 rounded-lg bg-oro-gold hover:bg-oro-gold/90 shadow-lg text-black font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? 'Loading...' : cta}
          </button>
        </div>
      </div>
    </div>
  );
}
